<script setup>
// Astra DS · NavSheet — la navegación en móvil.
//
// Figma: `Móvil/Menú abierto` 814:4032, panel 814:4065.
//
// 🔴 **No es un modal centrado, es un panel pegado a la derecha.** 330 de los
// 390, dejando 60 por los que se ve la página que hay debajo. Esa franja no es
// decoración: dice que no te has ido a ninguna parte y da dónde tocar para
// volver.
//
// Sustituye al carril horizontal que había antes. Con seis secciones y una
// pantalla de 390, aquello era una lista que había que arrastrar para descubrir
// que existía: la mitad de los destinos no se veían nunca.
//
// El orden del nodo, de arriba abajo: cabecera con el rótulo y el cierre ·
// las seis secciones · el buzón, que va aparte porque no es una sección más
// sino una invitación · el relleno que empuja · y el pie, con lo que suena y
// las opciones.
import { ref, computed, watch, onBeforeUnmount, nextTick } from "vue";
import AstraIcon from "@/components/icons/AstraIcon.vue";
import ButtonDs from "@/components/ds/ButtonDs.vue";
import { useCuentaStore } from "@/stores/cuenta";

const props = defineProps({
  open: { type: Boolean, default: false },

  // [{ label, to }] — las mismas que la cabecera de escritorio.
  // La misma lista que la cabecera. Los marcados con `soloEscritorio` no bajan
  // aquí: ver `destinos`.
  items: { type: Array, default: () => [] },
  current: { type: String, default: "" },

  // El pie cuenta qué suena. Sin programa no se pinta la fila: una etiqueta
  // «Suena ahora» sobre un hueco es peor que no decir nada.
  programme: { type: String, default: "" },
  live: { type: Boolean, default: false },

  theme: { type: String, default: "dark" },

  // Las redes del pie. Llegan por prop y no se escriben aquí: el pie del sitio
  // ya las tiene, y dos listas que hay que acordarse de cambiar a la vez acaban
  // diciendo cosas distintas.
  social: { type: Array, default: () => [] },

  // Dónde escuchar: TuneIn, Spotify y las apps. En móvil esta columna
  // desaparece del pie y vive aquí — está a un toque en vez de al final de una
  // página larga.
  listen: { type: Array, default: () => [] },
});

// Los que sí bajan al panel. `soloEscritorio` deja fuera a Radar, que aquí ya
// tiene su celda propia al pie —«Radar →», junto al buzón— y ponerlo dos veces
// en la misma pantalla es ruido en el sitio donde menos sobra.
const destinos = computed(() =>
  props.items.filter((item) => !item.soloEscritorio),
);

const emit = defineEmits(["close", "toggle-theme"]);

// La sesión, para saber si la celda de cuenta dice «Entrar» o «Mi cuenta».
const cuenta = useCuentaStore();

const panel = ref(null);
const activo = (item) =>
  props.current === item.to ||
  (item.to !== "/" && String(props.current).startsWith(String(item.to))) ||
  (item.tambienEn || []).some((prefijo) => String(props.current).startsWith(prefijo));

// ── Lo que un panel a pantalla completa tiene que hacer ──
//
// El fondo no se mueve mientras el panel está abierto. Sin esto, arrastrar
// sobre el panel desplaza la página de debajo y al cerrar te has ido de sitio.
let scrollBloqueado = "";
function bloquearFondo(si) {
  if (typeof document === "undefined") return;
  if (si) {
    scrollBloqueado = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = scrollBloqueado;
  }
}

function alTeclado(e) {
  if (e.key === "Escape") emit("close");
}

watch(
  () => props.open,
  async (abierto) => {
    bloquearFondo(abierto);
    if (abierto) {
      document.addEventListener("keydown", alTeclado);
      // El foco entra en el panel: si se queda en el botón de abrir, que está
      // detrás, el teclado sigue navegando por la página tapada.
      await nextTick();
      panel.value?.focus();
    } else {
      document.removeEventListener("keydown", alTeclado);
    }
  },
);

onBeforeUnmount(() => {
  document.removeEventListener("keydown", alTeclado);
  bloquearFondo(false);
});
</script>

<template>
  <Teleport to="body">
    <!-- La franja de la izquierda cierra al tocarla. Es lo que se espera de un
         panel que deja ver lo de detrás. -->
    <div v-if="open" class="nav-sheet" @click.self="emit('close')">
      <nav
        ref="panel"
        class="nav-sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Menú"
        tabindex="-1"
      >
        <div class="nav-sheet__head">
          <p class="nav-sheet__kicker">Menú</p>
          <ButtonDs
            class="nav-sheet__close"
            variant="ghost"
            icono="close"
            :icono-tam="20"
            etiqueta="Cerrar el menú"
            @click="emit('close')"
          />
        </div>

        <ul class="nav-sheet__list">
          <li v-for="item in destinos" :key="item.to">
            <router-link
              class="nav-sheet__link"
              :class="{ 'is-on': activo(item) }"
              :to="item.to"
              @click="emit('close')"
            >
              <!-- Cuadrado, no punto ni subrayado: es la misma marca de «esto
                   está vivo» que usa la cabecera. -->
              <i v-if="activo(item)" class="nav-sheet__mark" aria-hidden="true" />
              {{ item.label }}
            </router-link>
          </li>
        </ul>

        <!-- ── La cuenta, en móvil ──
             El panel `AccountMenu` de la cabecera es solo web, así que aquí el
             acceso baja al menú con el molde del buzón. No se repite todo el
             panel: en una pantalla de 390 lo que hace falta es la puerta. -->
        <router-link
          class="nav-sheet__buzon nav-sheet__buzon--quiet"
          :to="cuenta.haySesion ? '/cuenta/area' : '/cuenta'"
          @click="emit('close')"
        >
          <span class="nav-sheet__buzon-title">
            {{ cuenta.haySesion ? "Mi cuenta →" : "Entrar →" }}
          </span>
          <span class="nav-sheet__buzon-sub">
            {{
              cuenta.haySesion
                ? cuenta.cuenta?.email || "Tu cuenta de Astra FM"
                : "Crear cuenta pide correo y contraseña. Nada más."
            }}
          </span>
        </router-link>

        <!-- El buzón no es una sección más: es lo único que se le pide a quien
             mira, así que va aparte y con su frase. -->
        <router-link
          class="nav-sheet__buzon"
          to="/radar"
          @click="emit('close')"
        >
          <span class="nav-sheet__buzon-title">Radar →</span>
          <span class="nav-sheet__buzon-sub">
            Manda tu música · el buzón está abierto
          </span>
        </router-link>

        <div class="nav-sheet__fill" />

        <!-- El pie, por categorías: primero qué suena, luego dónde escucharlo
             y por último dónde seguirnos. Cada bloque con su rótulo — sin él,
             «TuneIn» y «Bluesky» en la misma rejilla se leen como la misma
             clase de cosa, y no lo son: unos llevan a la radio y otros a
             hablar de ella. -->
        <div class="nav-sheet__foot">
          <div v-if="programme" class="nav-sheet__now">
            <div>
              <p class="nav-sheet__kicker">
                {{ live ? "Suena ahora" : "En pausa" }}
              </p>
              <p class="nav-sheet__now-name">{{ programme }}</p>
            </div>

            <!-- El tema va con el estado y no con las redes: es un ajuste de
                 la pantalla, no un destino. Mezclarlo con Bluesky lo convertía
                 en «una cosa más de la lista». -->
            <button
              class="nav-sheet__theme"
              type="button"
              :aria-label="
                theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'
              "
              @click="emit('toggle-theme')"
            >
              <AstraIcon name="contrast" :size="12" />
              Tema
            </button>
          </div>

          <div v-if="listen.length" class="nav-sheet__group">
            <p class="nav-sheet__kicker nav-sheet__group-title">Escuchar</p>
            <div class="nav-sheet__grid">
              <a
                v-for="sitio in listen"
                :key="sitio.label"
                class="nav-sheet__opt"
                :href="sitio.href"
                target="_blank"
                rel="noopener noreferrer"
                >{{ sitio.label }}</a
              >
            </div>
          </div>

          <div v-if="social.length" class="nav-sheet__group">
            <p class="nav-sheet__kicker nav-sheet__group-title">Social</p>
            <div class="nav-sheet__grid">
              <a
                v-for="red in social"
                :key="red.label"
                class="nav-sheet__opt"
                :href="red.href"
                target="_blank"
                rel="noopener noreferrer"
                >{{ red.label }}</a
              >
            </div>
          </div>
        </div>
      </nav>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.nav-sheet {
  position: fixed;
  inset: 0;
  // 🔴 Por encima del banner de cookies, que está en 9999.
  //
  // Con 120 el panel se pintaba debajo: se veía —el banner solo ocupa la franja
  // de abajo— pero el banner se llevaba los clics y la rueda en la zona que
  // cubre. Medido: `elementFromPoint` en el centro del panel devolvía el icono
  // de la cookie, no el menú. Ni se podía deslizar ni pulsar ahí.
  //
  // Es de esos fallos que una prueba automática no ve si hace `click()` por
  // JavaScript, porque eso se salta el hit-testing. Solo aparece pulsando de
  // verdad, que es lo que hace una persona.
  //
  // Se queda por debajo del modal de edad (10000), que es bloqueante: si ese
  // está delante, no hay nada que navegar todavía.
  z-index: 9999;
  display: flex;
  justify-content: flex-end;
  // Se oscurece lo de detrás, pero poco: la franja tiene que seguir leyéndose
  // como la página, no como un telón.
  background: rgb(0 0 0 / 55%);
}

.nav-sheet__panel {
  display: flex;
  flex-direction: column;
  width: 330px;
  max-width: calc(100% - 60px);
  height: 100%;
  box-sizing: border-box;
  // El lienzo, no la superficie elevada: el panel no flota sobre nada, ocupa
  // su sitio. Y ata el fondo explícitamente — transparente resolvería contra
  // lo que hubiera detrás.
  background: var(--color-bg-canvas);
  border-left: 1px solid var(--color-border-default);
  overflow-y: auto;
  // 🔴 `padding: 0` no es decorativo, es un reset obligatorio.
  //
  // Este panel es un `<nav>`, y astra-styles.scss —la capa legacy que C1
  // congeló— tiene un `nav { padding: 1.125rem }` global. Sin esto, el panel
  // heredaba 18px por los CUATRO lados: separaba todo del borde, empujaba las
  // filas hacia dentro y dejaba aire de sobra arriba y abajo. Medido: los
  // textos caían en x=39 en vez de x=20.
  //
  // Es el mismo fallo que ya documenta SiteHeader, y volvió a pasar porque el
  // componente nuevo también usa `<nav>`. Muere cuando C4 vacíe esa capa.
  padding: 0;

  &:focus {
    outline: none;
  }
}

.nav-sheet__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 8px 0 20px;
  border-bottom: 1px solid var(--color-border-default);
}

.nav-sheet__kicker {
  @include t.mono-micro;
  margin: 0;
  color: var(--color-text-secondary);
}

// 44 de lado: el área de toque de una hoja a pantalla completa. Lo demás sale
// del botón.
.nav-sheet__close {
  width: 44px;
  height: 44px;
  border: 0;
}

.nav-sheet__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

// 51 de alto por fila, del nodo. Por encima de los 44 que pide un objetivo
// táctil, que era lo que había que comprobar antes de copiar la medida.
.nav-sheet__link {
  @include t.display-item;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 51px;
  padding: 0 20px;
  border-bottom: 1px solid var(--color-border-default);
  color: var(--color-text-primary);
  text-decoration: none;

  &:hover {
    background: var(--color-bg-elevated);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

.nav-sheet__mark {
  flex: none;
  width: 7px;
  height: 7px;
  background: var(--color-action-primary-default);
}

// ⚠️ La sección activa arranca 17px más adentro que las demás, porque el
// cuadrado va EN EL FLUJO y empuja al texto. No es un descuido: es lo que hace
// el nodo —rectángulo en x=20, texto en x=37, y el resto de secciones en 20—.
//
// Sacarlo del flujo alinearía las siete, pero entonces el cuadrado se comería
// el margen de 20 o se saldría del panel. Si algún día molesta, la decisión es
// de diseño y se toma en la Library, no aquí.

.nav-sheet__buzon {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border-default);
  color: var(--color-text-primary);
  text-decoration: none;

  &:hover {
    background: var(--color-bg-elevated);
  }
}

.nav-sheet__buzon-title {
  @include t.display-item;
  color: var(--color-text-accent);
}

// El rojo es del buzón, que es la invitación. La cuenta usa la misma celda en
// voz normal: si las dos gritan, ninguna destaca.
.nav-sheet__buzon--quiet .nav-sheet__buzon-title {
  color: var(--color-text-primary);
}

.nav-sheet__buzon-sub {
  @include t.mono-nano;
  color: var(--color-text-tertiary);
}

// Empuja el pie abajo cuando sobra alto, y desaparece cuando no.
.nav-sheet__fill {
  flex: 1 1 auto;
  min-height: 24px;
}

.nav-sheet__foot {
  border-top: 1px solid var(--color-border-default);
}

.nav-sheet__now {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
}

.nav-sheet__now-name {
  @include t.mono-label;
  margin: 3px 0 0;
  color: var(--color-text-primary);
}

.nav-sheet__theme {
  @include t.mono-nano;
  display: flex;
  flex: none;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 0 10px;
  border: 1px solid var(--color-border-default);
  border-radius: 0;
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;

  &:hover {
    border-color: var(--color-text-primary);
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
}

.nav-sheet__group {
  border-top: 1px solid var(--color-border-default);
}

.nav-sheet__group-title {
  padding: 12px 20px 6px;
}

// Rejilla de celdas con filete, como el resto del sistema: sin gutters, la
// línea de 1px es la separación. Dos columnas y no tres: con «App Android» o
// «Instagram» dentro, tres celdas en 330 dejan el texto partido.
.nav-sheet__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid var(--color-border-default);

  // La segunda de cada fila no lleva línea a la derecha.
  > :nth-child(2n) {
    border-right: 0;
  }

  // Y todas separan de la fila siguiente, salvo la última pareja.
  > * {
    border-bottom: 1px solid var(--color-border-default);
  }

  > :nth-last-child(-n + 2) {
    border-bottom: 0;
  }
}

// Alineadas a la izquierda como el resto del panel, no centradas. Con el texto
// al medio, cada celda empezaba en un sitio distinto y la columna dejaba de
// leerse como una lista: el ojo no encontraba dónde empieza cada cosa.
.nav-sheet__opt {
  @include t.mono-nano;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 20px;
  border: 0;
  border-right: 1px solid var(--color-border-default);
  border-radius: 0;
  background: none;
  color: var(--color-text-secondary);
  text-decoration: none;
  cursor: pointer;

  &:last-child {
    border-right: 0;
  }

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

@media (prefers-reduced-motion: no-preference) {
  .nav-sheet__panel {
    animation: nav-sheet-in 0.18s ease-out;
  }
}

@keyframes nav-sheet-in {
  from {
    transform: translateX(12px);
    opacity: 0.6;
  }
}
</style>
